import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { DdqSession } from '@/types/api.types';
import { formatSessionDate } from '../utils/formatSessionDate';

export interface ActiveWorkSectionProps {
  sessions: DdqSession[];
  className?: string;
}

export function ActiveWorkSection({ sessions, className }: ActiveWorkSectionProps) {
  const navigate = useNavigate();

  const needsAttention = useMemo(
    () => sessions.filter((s) => s.status === 'Ready'),
    [sessions]
  );

  const visible = needsAttention.slice(0, 3);

  // Filenames repeated within the visible slice need a date suffix, otherwise
  // duplicate uploads render as visually identical cards.
  const duplicateNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of visible) counts.set(s.fileName, (counts.get(s.fileName) ?? 0) + 1);
    return new Set([...counts].filter(([, n]) => n > 1).map(([name]) => name));
  }, [visible]);

  if (needsAttention.length === 0) return null;

  return (
    <div className={cn('space-y-3', className)}>
      <h2 className="text-sm font-medium text-muted-foreground">Needs your review</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((session) => {
          const dateLabel = formatSessionDate(session.created);
          const isDuplicate = duplicateNames.has(session.fileName);
          return (
          <Card
            key={session.id}
            className="hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate(`/sessions/${session.id}/review`)}
          >
            <CardContent className="py-3 px-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium truncate">
                      {session.fileName}
                      {isDuplicate && (
                        <span className="text-muted-foreground font-normal">
                          {' · '}{dateLabel}
                        </span>
                      )}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {session.totalQuestions} questions
                    <span className="mx-1">&middot;</span>
                    {session.automationRate.toFixed(0)}% auto-filled
                    <span className="mx-1">&middot;</span>
                    {dateLabel}
                  </p>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  className="shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/sessions/${session.id}/review`);
                  }}
                >
                  Continue Review
                  <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>
      {needsAttention.length > 3 && (
        <p className="text-xs text-muted-foreground">
          +{needsAttention.length - 3} more questionnaire{needsAttention.length - 3 !== 1 ? 's' : ''} ready for review
        </p>
      )}
    </div>
  );
}
